import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FilterField } from '@/components/list/filters';
import { DEFAULT_PAGE_SIZE, useListParams } from '@/components/list/use-list-params';
import { ALL_PERIODS, monthOf } from '../list/list-query';
import type { ReportsQuery } from './api';
import { REPORT_STATUSES, sortForOwner, type ReportStatus } from './types';
import { useReportsList, type ReportsListState } from './use-reports-list';

const FILTER_KEYS = ['period', 'status', 'owner', 'purpose', 'purposeLabel'] as const;

/** Adds two-place decimal-string amounts in whole cents, never through a fraction. */
export function sumAmounts(amounts: readonly string[]): string {
  const cents = amounts.reduce((sum, amount) => sum + Math.round(Number(amount) * 100), 0);
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

function useReportFields(state: ReportsListState, mayFilterByOwner: boolean) {
  const { t } = useTranslation('expenses');
  return useMemo<FilterField[]>(() => {
    const out: FilterField[] = [
      {
        key: 'period',
        label: t('list.filters.period'),
        monthPicker: true,
        options: [],
      },
      {
        key: 'status',
        label: t('list.filters.status'),
        options: REPORT_STATUSES.map((v) => ({ value: v, label: t(`reports.status.${v}`) })),
      },
    ];
    if (state.status === 'ready' && mayFilterByOwner) {
      out.push({
        key: 'owner',
        label: t('list.filters.owner'),
        options: state.owners.map((o) => ({ value: o.userId, label: o.displayName })),
      });
    }
    return out;
  }, [t, state, mayFilterByOwner]);
}

function periodOf(chosen: string | null, today: Date): string | null {
  if (chosen === null) return monthOf(today);
  return chosen === ALL_PERIODS ? null : chosen;
}

/** The list's filters (kept in the URL), its query, and what the page shows from the answer. */
export function useReportsView() {
  const { active, q, set, clear, params, pageSize, setPageSize, page, setPage, sort } =
    useListParams(FILTER_KEYS, true);
  const [today] = useState(() => new Date());
  const [ownerAllowed, setOwnerAllowed] = useState(false);
  const chosen = active['period'] ?? null;
  const period = periodOf(chosen, today);

  const query = useMemo<ReportsQuery>(() => {
    const status = active['status'];
    const owner = active['owner'];
    const [field, direction] = sort.split(':');
    const sortField =
      (['period', 'number', 'title', 'total', 'status'] as const).find(
        (value) => value === field,
      ) ?? 'period';
    return {
      q,
      ...(active['purpose'] ? { purpose: active['purpose'] } : {}),
      offset: (page - 1) * pageSize,
      ...(sort === ''
        ? {}
        : {
            sort: sortField,
            direction: direction === 'asc' ? ('asc' as const) : ('desc' as const),
          }),
      ...(period === null ? {} : { period }),
      ...(status ? { status: [status as ReportStatus] } : {}),
      ...(owner && ownerAllowed ? { owner } : {}),
      ...(pageSize === DEFAULT_PAGE_SIZE ? {} : { limit: pageSize }),
    };
  }, [active, period, ownerAllowed, pageSize, page, sort, q]);
  const { state, reload } = useReportsList(query);
  const mayFilterByOwner = state.status === 'ready' && state.can.filterByOwner === true;
  if (state.status === 'ready' && mayFilterByOwner !== ownerAllowed)
    setOwnerAllowed(mayFilterByOwner);

  const fields = useReportFields(state, mayFilterByOwner);

  const onFilter = useCallback(
    (key: string, value: string | null) =>
      set(key, key === 'period' && value === null ? ALL_PERIODS : value),
    [set],
  );

  const shown = useMemo(() => (state.status === 'ready' ? state.items : []), [state]);

  return {
    state,
    reload,
    active: { ...active, period },
    q,
    set,
    clear,
    params,
    pageSize,
    page,
    setPage,
    query,
    setPageSize,
    onFilter,
    fields,
    shown,
    desktop: useMemo(
      () => (sort === '' ? sortForOwner(shown, { reviewFirst: false }) : shown),
      [shown, sort],
    ),
    phone: useMemo(
      () => (sort === '' ? sortForOwner(shown, { reviewFirst: true }) : shown),
      [shown, sort],
    ),
    narrowed:
      (active['purpose'] ?? null) !== null ||
      q !== '' ||
      (active['status'] ?? null) !== null ||
      (active['owner'] ?? null) !== null,
    showOwner: mayFilterByOwner,
    canCreate: state.status === 'ready' && state.can.create !== false,
    creationMonth: period ?? monthOf(today),
  };
}
